import type { IConsumable } from '@experiences/interfaces';
import { UiText } from '@experiences/ui-common';
import { useLocalizedLinks } from '@experiences/util';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import { makeStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';

import {
    consumableCodeToApiKeyIdentifier,
    CONSUMABLES_WITHOUT_TENANT_VIEW_USAGE,
    consumableToLearnMoreLink,
    consumableToViewUsageMap,
} from '../../../../common/constants/Constant';
import { UiApiKey } from './UiApiKey';
import { UiConsumableCardLegend } from './UiConsumableCardLegend';
import { UiConsumablePool } from './UiConsumablePool';
import { useConsumptionDashboardLink } from './useConsumptionDashboardLink';
import { useViewUsageTelemetry } from './useViewUsageTelemetry';

const useStyles = makeStyles(theme =>
    createStyles({
        content: {
            display: 'flex',
            flexDirection: 'column',
            padding: '12px',
            height: '100%',
            gap: '4px',
            justifyContent: 'flex-start',
        },
        header: {
            fontSize: '14px',
            lineHeight: '20px',
            fontWeight: 600,
            color: theme.palette.semantic.colorForeground,
        },
        cardBottom: {
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            flexGrow: 1,
        },
        cardLink: {
            display: 'flex',
            flexDirection: 'row',
            fontSize: '14px',
            fontWeight: 600,
            marginTop: '10px',
        },
        link: {
            color: theme.palette.semantic.colorForegroundLink,
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: '14px',
            lineHeight: '20px',
            padding: '0 24px 0 0',
            '&:hover': { textDecoration: 'underline' },
        },
        selected: {
            borderLeft: `1px solid ${theme.palette.semantic.colorBorder}`,
            borderRight: `1px solid ${theme.palette.semantic.colorBorder}`,
            borderTop: `1px solid ${theme.palette.semantic.colorBorder}`,
        },
    }),
);

export const UiConsumableCard: React.FC<{
    consumable: IConsumable;
    handleOpenChart?: (selected: string) => void;
    isSelected?: boolean;
    tenantId?: string;
    order?: number;
    hideViewUsageButton?: boolean;
}> = ({
    consumable, handleOpenChart, isSelected, tenantId, order, hideViewUsageButton = false,
}) => {
    const classes = useStyles();
    const { formatMessage: translate } = useIntl();
    const getLocalizedLink = useLocalizedLinks();
    const {
        hasConsumptionDashboard, enableConsumptionDashboard, consumptionDashboardHref,
    } = useConsumptionDashboardLink(consumable.code, tenantId);

    const handleTelemetryForViewUsage = useViewUsageTelemetry(consumable.code, tenantId);

    const shouldHaveApiKey = useMemo(() =>
        consumableCodeToApiKeyIdentifier[consumable.code] !== undefined
    , [ consumable.code ]);

    return (<Grid
        key={consumable.code}
        item
        xs={6}
        data-cy="licenses-grid-item"
        order={order}>
        <div
            className={`${classes.content} ${isSelected ? classes.selected : ''}`}
            data-cy={`product-allocation-${consumable.code.toLowerCase()}`}>
            <div>
                <UiText
                    className={classes.header}
                    data-cy="licenseType"
                    role="heading"
                    aria-level={2}>
                    {translate({ id: `CLIENT_CONSUMABLE_USAGE_HEADER_${consumable.code}` })}
                </UiText>
            </div>
            <div>
                {consumable.pools.map(consumptionPool => (
                    <UiConsumablePool
                        key={consumptionPool.name}
                        consumableCode={consumable.code}
                        pool={consumptionPool} />
                ))}
                <UiConsumableCardLegend />
            </div>
            <div className={classes.cardBottom}>
                {shouldHaveApiKey && <UiApiKey
                    consumableCode={consumable.code}
                    apiKey={consumable.apiKey}
                    tenantId={tenantId} />}
                <div className={classes.cardLink}>
                    {handleOpenChart && consumableToViewUsageMap[consumable.code] && !hideViewUsageButton &&
                        !(tenantId && CONSUMABLES_WITHOUT_TENANT_VIEW_USAGE.includes(consumable.code)) &&
                  <Link
                      data-cy={`view-usage-link-${consumable.code}`}
                      aria-label={`${translate({ id: 'CLIENT_CONSUMABLE_VIEW_USAGE' })} - ${translate({ id: `CLIENT_CONSUMABLE_USAGE_HEADER_${consumable.code}` })}`}
                      className={classes.link}
                      onClick={() => {
                          handleOpenChart(consumable.code);
                          handleTelemetryForViewUsage();
                      }}
                      tabIndex={0}
                      underline='none'
                      aria-expanded={isSelected}>
                      {translate({ id: 'CLIENT_CONSUMABLE_VIEW_USAGE' })}
                  </Link>}
                    {hasConsumptionDashboard && enableConsumptionDashboard && !hideViewUsageButton &&
                  <Link
                      data-cy={`view-usage-preview-link-${consumable.code}`}
                      aria-label={`${translate({ id: 'CLIENT_CONSUMABLE_VIEW_USAGE_PREVIEW' })} - ${translate({ id: `CLIENT_CONSUMABLE_USAGE_HEADER_${consumable.code}` })}`}
                      className={classes.link}
                      href={consumptionDashboardHref}
                      underline='none'>
                      {translate({ id: 'CLIENT_CONSUMABLE_VIEW_USAGE_PREVIEW' })}
                  </Link>}
                    {consumableToLearnMoreLink[consumable.code] &&
                  <a
                      className={classes.link}
                      target="_blank"
                      rel="noreferrer"
                      href={getLocalizedLink(consumableToLearnMoreLink[consumable.code])}
                      data-cy="learn-more-link"
                      aria-label={`${translate({ id: 'CLIENT_LEARN_MORE' })} - ${translate({ id: `CLIENT_CONSUMABLE_USAGE_HEADER_${consumable.code}` })}`}
                  >
                      {translate({ id: 'CLIENT_LEARN_MORE' })}
                  </a>}
                </div>
            </div>
        </div>
    </Grid>);

};
